import React, { useMemo, useState, useRef, useEffect } from 'react'
import { Row, Form, Tab, Tabs } from 'react-bootstrap'
import InputField from '@components/common/InputField/InputField.component'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { usePageContentGridScrollMaxHeight } from '@hooks/usePageContentGridScrollMaxHeight'
import StackedColumnChart from '@components/common/StackedColumnChart/StackedColumnChart.component'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { v4 as uuid } from 'uuid'
import {
  getCoverageLevelText,
  getDataSourceText,
  getNetworkTypeText,
  getVisitPeriodUnitText,
} from '@utils/insurance.util'
import SearchWithFilter, {
  FilterSelection,
} from '@components/common/SearchWithFilter/SearchWithFilter.component'
import {
  ActionEventArgs,
  GridComponent,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import InsuranceSkeleton from '@pages/Insurance/InsuranceSkeleton.page'

interface PlanCoverageProps {
  insurancePlan: InsurancePlan
  carriers: any[]
  onChangeCarrier: (index: number) => void
  uuId: React.MutableRefObject<string>
  isPending?: boolean
}

const getServiceCategoryValue = (item: unknown): string => {
  if (typeof item === 'string') return item
  if (item && typeof item === 'object') {
    return Object.values(item as Record<string, unknown>).join('')
  }
  return ''
}

const formatServiceCategoryLabel = (category: string): string =>
  category.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

const StableInput = React.memo(function StableInput({
  searchQuery,
  setSearchQuery,
  setSearchFilters,
  clearAllFilters,
  filter,
}: {
  searchQuery: string
  setSearchQuery: (value: string) => void
  setSearchFilters: (filters: FilterSelection) => void
  clearAllFilters: () => void
  filter: {
    data: any
    labels: any
  }
}) {
  const inputElement = document.querySelector(
    '.search-insurance-filter-container input.search-input',
  )
  if (inputElement) {
    ;(inputElement as HTMLInputElement).focus()
  }
  return (
    <div
      className="search-insurance-filter-container"
      style={{
        width: '220px',
        position: 'absolute',
        padding: '0',
        right: '14px',
        top: '8px',
      }}
    >
      <SearchWithFilter
        searchIcon="mgc_search_2_line"
        filterIcon="mgc_settings_2_line"
        filterLabel=""
        placeholder="Search..."
        searchQuery={searchQuery}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setSearchQuery(e.target.value)
        }}
        onFilterChange={(selectedFilters: FilterSelection) => {
          setSearchFilters(selectedFilters)
        }}
        onReset={clearAllFilters}
        filters={filter.data}
        filterLabels={filter.labels}
      />
    </div>
  )
})

const chartDefaultData = {
  info: [
    {
      data: [],
      barColor: '#185DD8',
      barBorderColor: 'white',
      dataLabel: {
        fontColor: 'white',
        position: 'Middle',
      },
    },
    {
      data: [],
      barColor: '#97C9F9',
      barBorderColor: 'white',
      dataLabel: {
        fontColor: 'black',
        position: 'Middle',
      },
    },
  ],
  chartHeight: '384px',
  maximumValue: 100,
  labelFormat: '{value}%',
}

const CoverageGridScrollSection = ({
  children,
  rowCount,
  remountKey,
}: {
  children: React.ReactNode
  rowCount: number
  remountKey: string
}): React.ReactElement => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const scrollStyle = usePageContentGridScrollMaxHeight(
    sectionRef,
    [rowCount, remountKey],
    { disabled: rowCount === 0 },
  )

  return (
    <div
      ref={sectionRef}
      className="grid-container page-grid-scroll-section"
      style={scrollStyle}
    >
      {children}
    </div>
  )
}

const PlanCoverage: React.FC<PlanCoverageProps> = ({
  insurancePlan,
  carriers,
  onChangeCarrier,
  uuId,
  isPending = false,
}) => {
  const [serviceCategory, setServiceCategory] = useState<string | undefined>(
    undefined,
  )
  const [tab, setTab] = useState<string | undefined>('benefits')
  const [rerender, setRerender] = useState(0)
  const searchProcedureCodeRef = useRef<string | null>(null)
  const isProceduresProcessing = useRef<boolean>(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchFilters, setSearchFilters] = useState<FilterSelection>({})
  const [chartData, setChartData] = useState<any>(chartDefaultData)
  const gridRef = useRef<any>({
    benefits: useRef<GridComponent>(null),
    deductibles: useRef<GridComponent>(null),
    maximums: useRef<GridComponent>(null),
  })

  const forceRerender = (): void => {
    setRerender((prev) => prev + 1)
  }

  const columnsBenefits = useMemo(
    () => [
      {
        field: 'procedure_code',
        headerText: 'Code',
        width: '70px',
      },
      {
        field: 'procedure_description',
        headerText: 'Description',
        width: 'auto',
        template: (props: any) => (
          <TooltipComponent
            content={props.procedure_description}
            opensOn="Hover"
            cssClass="e-tooltip-template-css e-tooltip-tab-feature"
            position="TopCenter"
          >
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90%',
                display: 'block',
                fontSize: '0.875rem',
              }}
            >
              {props.procedure_description}
            </span>
          </TooltipComponent>
        ),
      },
      {
        field: 'network_type',
        headerText: 'Network',
        width: '120px',
        template: (data: any): JSX.Element => {
          return <span>{getNetworkTypeText(data.network_type)}</span>
        },
      },
      {
        field: 'co_insurance',
        headerText: 'Coinsurance',
        width: '130px',
        template: (data: any): JSX.Element => {
          return <span>{data.co_insurance}%</span>
        },
      },
      {
        field: 'co_pay',
        headerText: 'Copay',
        width: '90px',
        template: (data: any): JSX.Element => {
          if (data.co_pay) {
            return <span>${data.co_pay}</span>
          }
          return <span>-</span>
        },
      },
      {
        field: 'age_maximum',
        headerText: 'Age max.',
        width: '100px',
        template: (data: any): JSX.Element => {
          if (data.age_maximum) {
            return <span>{data.age_maximum}</span>
          }
          return <span>-</span>
        },
      },
      {
        field: 'visit_limit',
        headerText: 'Visit limit',
        width: '100px',
        template: (data: any): JSX.Element => {
          if (data.visit_limit) {
            return <span>{data.visit_limit}</span>
          }
          return <span>-</span>
        },
      },
      {
        field: 'visit_period',
        headerText: 'Visit period',
        width: '110px',
        template: (data: any): JSX.Element => {
          if (data.visit_period) {
            return (
              <span>
                {data.visit_period}{' '}
                {getVisitPeriodUnitText(data.visit_period_unit)}
                {Number(data.visit_period) > 1 ? 's' : ''}
              </span>
            )
          }
          return <span>-</span>
        },
      },
    ],
    [],
  )

  const columnsDeductibles = useMemo(
    () => [
      {
        field: 'network',
        headerText: 'Network',
        width: 'auto',
      },
      {
        field: 'network_type',
        headerText: 'Type',
        width: '120px',
        template: (data: any): JSX.Element => {
          return <span>{getNetworkTypeText(data.network_type)}</span>
        },
      },
      {
        field: 'coverage_level',
        headerText: 'Coverage level',
        width: '130px',
        template: (data: any): JSX.Element => {
          return <span>{getCoverageLevelText(data.coverage_level)}</span>
        },
      },
      {
        field: 'amount',
        headerText: 'Amount',
        width: '110px',
        template: (data: any): JSX.Element => {
          return <span>${data.amount}</span>
        },
      },
      {
        field: 'remaining',
        headerText: 'Remaining',
        width: '110px',
        template: (data: any): JSX.Element => {
          return <span>${data.remaining}</span>
        },
      },
      {
        field: 'period_unit',
        headerText: 'Period unit',
        width: '110px',
        template: (data: any): JSX.Element => {
          return <span>{getVisitPeriodUnitText(data.period_unit)}</span>
        },
      },
    ],
    [],
  )

  const columnsMaximums = useMemo(
    () => [
      {
        field: 'network',
        headerText: 'Network',
        width: 'auto',
      },
      {
        field: 'network_type',
        headerText: 'Type',
        width: '120px',
        template: (data: any): JSX.Element => {
          return <span>{getNetworkTypeText(data.network_type)}</span>
        },
      },
      {
        field: 'coverage_level',
        headerText: 'Coverage level',
        width: '130px',
        template: (data: any): JSX.Element => {
          return <span>{getCoverageLevelText(data.coverage_level)}</span>
        },
      },
      {
        field: 'amount',
        headerText: 'Amount',
        width: '110px',
        template: (data: any): JSX.Element => {
          return <span>${data.amount}</span>
        },
      },
      {
        field: 'remaining',
        headerText: 'Remaining',
        width: '110px',
        template: (data: any): JSX.Element => {
          return <span>${data.remaining}</span>
        },
      },
      {
        field: 'period_unit',
        headerText: 'Period unit',
        width: '110px',
        template: (data: any): JSX.Element => {
          return <span>{getVisitPeriodUnitText(data.period_unit)}</span>
        },
      },
    ],
    [],
  )

  const filterData = (selectedTab: string): any => {
    const networkTypes = ['In-network', 'Out-of-network']
    const coverageLevels = ['Family', 'Individual', 'Unknown']
    const visitPeriodUnits = [
      'Year',
      'Month',
      'Day',
      'Lifetime',
      'Visit',
      'Coverage Year',
    ]
    const sources = ['EDI', 'Payer Portal']

    return {
      network_type: networkTypes,
      ...(selectedTab !== 'benefits' ? { coverage_level: coverageLevels } : {}),
      ...(selectedTab === 'benefits'
        ? { visit_period_unit: visitPeriodUnits }
        : { period_unit: visitPeriodUnits }),
      source: sources,
    }
  }

  const filterLabels = (selectedTab: string): any => {
    return {
      network_type: 'Network type',
      ...(selectedTab !== 'benefits'
        ? { coverage_level: 'Coverage level' }
        : {}),
      ...(selectedTab === 'benefits'
        ? { visit_period_unit: 'Visit period unit' }
        : { period_unit: 'Period unit' }),
      source: 'Source',
    }
  }

  const selectServiceCategory = (category: string): void => {
    if (!category || category === serviceCategory) return
    uuId.current = uuid()
    setServiceCategory(category)
  }

  const handleSearchProcedure = (e: any): void => {
    if (e.key === 'Enter') {
      isProceduresProcessing.current = true
      if (e.target.value.toLowerCase().trim() !== '') {
        const data =
          insurancePlan?.services?.flatMap((service) => service.benefits) || []
        const filterFields = ['procedure_code', 'procedure_description']
        const procedures = data.filter((item) => {
          const query = e.target.value.toLowerCase().trim()
          return filterFields.some((field) => {
            const value = item[field]
            const valueAsString =
              value != null ? value.toString().toLowerCase() : ''
            return valueAsString.includes(query)
          })
        })
        if (procedures && procedures.length > 0) {
          const serviceTypeNameCurrent = procedures[0].category
          if (serviceTypeNameCurrent !== undefined) {
            setTab('benefits')
            setTimeout(() => {
              selectServiceCategory(serviceTypeNameCurrent)
              searchProcedureCodeRef.current = e.target.value
                .toLowerCase()
                .trim()
              forceRerender()
            }, 300)
          }
        }
      } else {
        searchProcedureCodeRef.current = null
        forceRerender()
      }
      isProceduresProcessing.current = false
    }
  }

  if (
    serviceCategory === undefined &&
    insurancePlan?.services &&
    insurancePlan?.services?.length > 0
  ) {
    setServiceCategory(insurancePlan?.services?.[0].category)
  }

  const clearAllFilters = (): void => {
    setSearchFilters({})
    setSearchQuery('')
  }
  const displayData = (selectedTab: string): any[] => {
    let data = []
    switch (selectedTab) {
      case 'benefits':
        data =
          insurancePlan?.services?.find((x) => x.category === serviceCategory)
            ?.benefits || []
        break
      case 'deductibles':
        data =
          insurancePlan?.services?.find((x) => x.category === serviceCategory)
            ?.deductibles || []
        break
      case 'maximums':
        data =
          insurancePlan?.services?.find((x) => x.category === serviceCategory)
            ?.maximums || []
        break
    }

    if (searchProcedureCodeRef.current !== null && selectedTab === 'benefits') {
      const filterFields = ['procedure_code', 'procedure_description']
      data = data.filter((item) => {
        const query = searchProcedureCodeRef.current
        return filterFields.some((field) => {
          const value = item[field]
          const valueAsString =
            value != null ? value.toString().toLowerCase() : ''

          return valueAsString.includes(query)
        })
      })
    }

    if (searchQuery && searchQuery !== '') {
      const filterFields = [
        'procedure_code',
        'procedure_description',
        'network',
      ]
      data = data.filter((item) => {
        const query = searchQuery.toLowerCase()
        return filterFields.some((field) => {
          const value = item[field]
          const valueAsString =
            value != null ? value.toString().toLowerCase() : ''

          return valueAsString.includes(query)
        })
      })
    }

    if (searchFilters.network_type && searchFilters.network_type.length > 0) {
      data = data.filter((x) => {
        const networkType = getNetworkTypeText(x.network_type)
        return searchFilters.network_type.includes(networkType || '')
      })
    }

    if (
      searchFilters.coverage_level &&
      searchFilters.coverage_level.length > 0 &&
      selectedTab !== 'benefits'
    ) {
      data = data.filter((x) => {
        const coverageLevel = getCoverageLevelText(x.coverage_level)
        return searchFilters.coverage_level.includes(coverageLevel || '')
      })
    }

    if (
      searchFilters.visit_period_unit &&
      searchFilters.visit_period_unit.length > 0
    ) {
      data = data.filter((x) => {
        const visitPeriodUnit = getVisitPeriodUnitText(x.visit_period_unit)
        return searchFilters.visit_period_unit.includes(visitPeriodUnit || '')
      })
    }

    if (
      searchFilters.period_unit &&
      searchFilters.period_unit.length > 0 &&
      selectedTab !== 'benefits'
    ) {
      data = data.filter((x) => {
        const periodUnit = getVisitPeriodUnitText(x.period_unit)
        return searchFilters.period_unit.includes(periodUnit || '')
      })
    }

    if (searchFilters.source && searchFilters.source.length > 0) {
      data = data.filter((x) => {
        const source = getDataSourceText(x.source)
        return searchFilters.source.includes(source || '')
      })
    }

    return data
  }

  const handleRowSelected = (args: RowSelectEventArgs): void => {
    let labelFormat = '${value}'
    let data1 = []
    let data2 = []
    let maximumValue = 100
    const argsData = Array.isArray(args?.data) ? args.data[0] : args?.data || {}
    if (argsData === undefined) {
      setChartData(chartDefaultData)
      return
    }
    if ('insurance_benefit_id' in (argsData ?? {})) {
      labelFormat = '{value}%'
      data1 = [{ x: 'Insurance coverage', y: (argsData as any).co_insurance }]
      data2 = [
        {
          x: 'Patient responsibility',
          y: 100 - ((argsData as any).co_insurance || 0),
        },
      ]
    } else {
      data1 = [{ x: 'Amount', y: (argsData as any).amount }]
      data2 = [{ x: 'Remaining', y: (argsData as any).remaining }]
      maximumValue = (argsData as any).amount
    }

    setChartData({
      ...chartDefaultData,
      info: [
        {
          ...chartDefaultData.info[0],
          data: data1,
        },
        {
          ...chartDefaultData.info[1],
          data: data2,
        },
      ],
      maximumValue: maximumValue,
      labelFormat: labelFormat,
    })
  }

  const stackedColumnChart = (): JSX.Element => {
    return <StackedColumnChart chartdata={chartData} />
  }

  const selectRow = (index: number): void => {
    setChartData(chartDefaultData)
    try {
      gridRef.current?.benefits?.current?.selectRows([])
      gridRef.current?.deductibles?.current?.selectRows([])
      gridRef.current?.maximums?.current?.selectRows([])
    } catch {}

    try {
      setTimeout(() => {
        switch (tab) {
          case 'benefits':
            gridRef.current?.benefits?.current?.selectRows([index])
            break
          case 'deductibles':
            gridRef.current?.deductibles?.current?.selectRows([index])
            break
          case 'maximums':
            gridRef.current?.maximums?.current?.selectRows([index])
            break
        }
      }, 500)
    } catch {}
  }

  useEffect(() => {
    selectRow(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    serviceCategory,
    insurancePlan?.insurance_plan_id,
    searchQuery,
    searchFilters,
    rerender,
  ])

  const handleActionComplete = (args: ActionEventArgs): void => {
    if (args.requestType === 'paging') {
      selectRow(0)
    }
  }

  useEffect(() => {
    if (searchProcedureCodeRef.current === null) {
      setTimeout(() => {
        const inputSelector = 'input.insurance-search-procedure'
        const inputElement = document.querySelector(
          inputSelector,
        ) as HTMLInputElement
        if (inputElement) {
          inputElement.value = ''
          inputElement.focus()
        }
      }, 100)
    }
  }, [rerender])

  const renderCoverageGrid = (
    tabKey: 'benefits' | 'deductibles' | 'maximums',
    columns: typeof columnsBenefits,
    emptyTitle: string,
    emptyDescription: string,
  ): React.ReactElement => {
    const rows = displayData(tabKey)
    if (rows.length === 0) {
      return (
        <InsuranceEmptyCard
          iconClass="mgc mgc_shield_fill"
          emptyTitle={emptyTitle}
          description={emptyDescription}
          outlined={false}
        />
      )
    }
    return (
      <CoverageGridScrollSection
        rowCount={rows.length}
        remountKey={`${tabKey}-${serviceCategory}-${searchQuery}-${rerender}`}
      >
        <DataGridComponent
          ref={gridRef.current?.[tabKey]}
          data={rows}
          columns={columns}
          allowPaging={true}
          allowSorting={true}
          allowTextWrap={true}
          selectionSettings={{
            type: 'Single',
            mode: 'Row',
            enableToggle: false,
          }}
          rowSelected={handleRowSelected}
          pageSize={10}
          onActionComplete={handleActionComplete}
        />
      </CoverageGridScrollSection>
    )
  }

  const serviceCategories = (insurancePlan?.categories?.[tab!] ?? []).map(
    getServiceCategoryValue,
  )

  if (isPending) {
    return <InsuranceSkeleton variant="coverage" />
  }

  return (
    <div className="section-insurance-coverage">
      <div className="coverage-layout">
        <aside className="coverage-side-panel-wrapper">
          <div className="coverage-side-panel">
            <div className="coverage-side-panel-heading">
              <span className="coverage-side-panel-title">
                Service type coverage
              </span>
            </div>
            <div className="coverage-side-panel-controls">
              <div className="coverage-side-panel-search">
                <InputField
                  placeholder="Search procedures"
                  handleKeyDown={handleSearchProcedure}
                  leftIcon={<span className="mgc_search_line" />}
                  className="insurance-search-procedure"
                  isControlled={false}
                  disabled={searchProcedureCodeRef.current !== null}
                />
                {searchProcedureCodeRef.current !== null && (
                  <div
                    key={searchProcedureCodeRef.current}
                    className="selected-item"
                  >
                    {searchProcedureCodeRef.current}
                    <button
                      type="button"
                      className="unstyled-button selected-item-remove ti ti-x"
                      aria-label="Clear procedure search"
                      onClick={() => {
                        handleSearchProcedure({
                          key: 'Enter',
                          target: { value: '' },
                        })
                      }}
                    ></button>
                  </div>
                )}
              </div>
              <Form.Group controlId="formCarrierSelect">
                <Form.Label>Carrier name</Form.Label>
                <Form.Select
                  name="carrier"
                  value={insurancePlan?.insurance_plan_id}
                  onChange={(e) => onChangeCarrier(e.target.selectedIndex)}
                >
                  {carriers.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="coverage-sidebar-group">
              {serviceCategories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`coverage-sidebar-item${
                    serviceCategory === category ? ' active' : ''
                  }`}
                  onClick={() => selectServiceCategory(category)}
                >
                  {formatServiceCategoryLabel(category)}
                </button>
              ))}
            </div>
          </div>
        </aside>
        <div className="deductions-limitations-col">
          <Row key={uuid()} style={{ marginTop: '16px' }}>
            {stackedColumnChart()}
          </Row>
          <Row style={{ position: 'relative' }}>
            <StableInput
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              setSearchFilters={setSearchFilters}
              clearAllFilters={clearAllFilters}
              filter={{
                data: filterData(tab!),
                labels: filterLabels(tab!),
              }}
            />
            <Tabs
              mountOnEnter
              unmountOnExit
              activeKey={tab}
              onSelect={(tKey) => {
                if (tKey !== null) {
                  setTab(tKey)
                }
              }}
            >
              <Tab eventKey="benefits" title={<>Benefits</>}>
                {renderCoverageGrid(
                  'benefits',
                  columnsBenefits,
                  'No benefits found',
                  "You don't have any benefits on record for this service type.",
                )}
              </Tab>
              <Tab eventKey="deductibles" title={<>Deductibles</>}>
                {renderCoverageGrid(
                  'deductibles',
                  columnsDeductibles,
                  'No deductibles found',
                  "You don't have any deductibles on record for this service type.",
                )}
              </Tab>
              <Tab eventKey="maximums" title={<>Maximums</>}>
                {renderCoverageGrid(
                  'maximums',
                  columnsMaximums,
                  'No maximums found',
                  "You don't have any maximums on record for this service type.",
                )}
              </Tab>
            </Tabs>
          </Row>
        </div>
      </div>
    </div>
  )
}

export default PlanCoverage
